"use client";

import { ReactNode, useMemo, useState } from "react";
import Image from "next/image";
import { cn } from "~/lib/utils";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Carousel from "~/componentsDS/Carousel";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { ArrowLeftIcon, ArrowRightIcon, HandSwipeLeftIcon, HandSwipeRightIcon } from "@phosphor-icons/react/ssr";

import type { GetStartedStep } from "../types";
import Fade from "embla-carousel-fade";
import { DotButton, useDotButton } from "~/componentsND/carousel/CarouselDotButton";
import usePrevNextButtons from "~/componentsND/carousel/usePrevNextButtons";

type MobileCarouselProps = {
    steps: GetStartedStep[];
    productBadge?: ReactNode;
};

const GRADIENT_BG = "linear-gradient(180deg, #19223D 2.38%, #2A3966 39.12%, #A9B9E5 161.17%)";

const MobileCarousel = ({ steps, productBadge }: MobileCarouselProps) => {
    const fadePlugins = useMemo(() => [Fade()], []);
    const [emblaApi, setEmblaApi] = useState<CarouselApi>();

    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

    return (
        <div className="md:hidden">
            <div className="rounded-2xl overflow-hidden" style={{ background: GRADIENT_BG }}>
                <Carousel
                    plugins={fadePlugins}
                    setApi={setEmblaApi}
                    loop={false}
                    arrows={false}
                    dots={false}
                    opts={{
                        align: "start",
                        skipSnaps: false,
                        dragFree: false,
                    }}
                    className="w-full"
                >
                    <CarouselContentVisible className="ml-0">
                        {steps.map((step, index) => (
                            <CarouselItem
                                key={step.id}
                                className="pl-0 basis-full h-[500px]"
                            >
                                <div className="relative flex flex-col h-full">
                                    {/* Swipe hint */}
                                    <div className="absolute top-6 right-4 z-10 flex items-center gap-2 text-pureWhite">
                                        {index === 0 && (
                                            <Paragraph weight="500" className="translate-y-[2px]">Swipe To Read</Paragraph>
                                        )}
                                        {index === steps.length - 1 ? (
                                            <HandSwipeLeftIcon size={20} />
                                        ) : (
                                            <HandSwipeRightIcon size={20} />
                                        )}
                                    </div>

                                    {/* Image */}
                                    <div className="relative flex-1 flex items-start justify-center overflow-hidden pt-[80px]">
                                        <Image
                                            src={step.image.mobile.url}
                                            alt={step.image.mobile.alt}
                                            width={step.image.mobile.width}
                                            height={0}
                                            sizes="100vw"
                                            quality={95}
                                            className={cn("h-auto max-w-none shrink-0", step.image.mobile.className)}
                                            style={{ width: `${step.image.mobile.width}px`, minWidth: `${step.image.mobile.width}px` }}
                                        />
                                    </div>

                                    {/* Info card */}
                                    <div className="px-3 pb-6 -mt-16 relative z-10">
                                        <div className="
                                            bg-pureWhite rounded-3xl px-6 py-4
                                            max-w-[400px] mx-auto
                                            shadow-[-18px_24px_8px_0_rgba(0,0,0,0.00),_-12px_15px_8px_0_rgba(0,0,0,0.01),_-7px_9px_6px_0_rgba(0,0,0,0.05),_-3px_4px_5px_0_rgba(0,0,0,0.09),_-1px_1px_3px_0_rgba(0,0,0,0.10)]
                                        ">
                                            {productBadge && (
                                                <div className="mb-2.5 w-[134px]">{productBadge}</div>
                                            )}
                                            <div className="flex items-start gap-3">
                                                <div className="
                                                    inline-flex justify-center items-center shrink-0 w-7 h-7 pt-1
                                                    font-suisseBP text-[15.75px] leading-[1.1] text-pureWhite
                                                    bg-primary300
                                                    rounded-full overflow-hidden
                                                ">
                                                    {index + 1}
                                                </div>
                                                <div className="flex flex-col gap-2 text-primary500">
                                                    <Heading
                                                        as="h3"
                                                        size="xs"
                                                        weight="400"
                                                    >
                                                        {step.title}
                                                    </Heading>

                                                    <Paragraph>{step.mobileDescription ?? step.description}</Paragraph>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContentVisible>
                </Carousel>
            </div>

            {/* Pagination — below gradient */}
            <div className="flex items-center justify-center gap-6 mt-6">
                <button onClick={onPrevButtonClick} disabled={prevBtnDisabled} className="p-2 disabled:text-gray300">
                    <ArrowLeftIcon size={24} />
                </button>
                <div className="flex gap-2">
                    {scrollSnaps.map((_: number, index: number) => (
                        <DotButton
                            key={index}
                            onClick={() => onDotButtonClick(index)}
                            className={cn(
                                "embla__dot h-[6px] rounded transition-all duration-300",
                                index === selectedIndex
                                    ? "bg-primary300 w-[6px]"
                                    : "bg-[#9AB1F5] w-[6px]"
                            )}
                        />
                    ))}
                </div>
                <button onClick={onNextButtonClick} disabled={nextBtnDisabled} className="p-2 disabled:text-gray300">
                    <ArrowRightIcon size={24} />
                </button>
            </div>
        </div>
    );
};

export default MobileCarousel;
